import { format } from "date-fns";
import { TFunction } from "react-i18next";

import { File } from "../models/file";
import { Folder } from "../models/folder";
import { Order } from "../models/order";
import { Recording } from "../models/recording";
import { Sales } from "../models/sales";
import { FileType, FolderName, FolderType } from "./enums";

export function getOrderRecordFiles(
  translate: TFunction<"translation", undefined>,
  records: Recording[],
  date: string,
  quoteFolders: Folder[],
  setQuoteFolders: Function
) {
  const recordVideoSurvey: File[] = [];

  records.forEach((record, recordIndex: number) => {
    recordVideoSurvey.push({
      name: `${format(new Date(date), "dd/MM/yyyy hh:mm aa")}${
        records.length > 1 ? ` - Video ${recordIndex + 1}` : ""
      }`,
      type: "video",
      uri: record?.link,
      extension: "mp4",
      isAllowedDelete: false,
      isAllowedEdit: false,
    });
  });

  const folders = quoteFolders;
  const foldersResultIndex = folders.findIndex(
    (folder) => folder.name?.toLowerCase() === FolderName.VIDEOS
  );
  const foldersResult = folders?.find((folder) => folder.name?.toLowerCase() === FolderName.VIDEOS);

  if (foldersResult) {
    const videoSurveyFolder = foldersResult.folders;
    const findVideoSurveyFolder = videoSurveyFolder?.find(
      (folder) => folder.name === FolderName.VIDEO_SURVEY
    );
    const videoSurveyFolderIndex = foldersResult?.folders?.findIndex(
      (folder) => folder.name === FolderName.VIDEO_SURVEY
    );

    if (videoSurveyFolder && findVideoSurveyFolder) {
      const videos = findVideoSurveyFolder.files
        ? [...findVideoSurveyFolder.files, ...recordVideoSurvey]
        : recordVideoSurvey;

      findVideoSurveyFolder.files = videos.sort();
      if (videoSurveyFolderIndex !== undefined) {
        videoSurveyFolder[videoSurveyFolderIndex] = findVideoSurveyFolder;
        foldersResult.folders = videoSurveyFolder;
        folders[foldersResultIndex] = foldersResult;
        setQuoteFolders([...folders]);
      }
    } else {
      const videoSurvey = {
        name: translate("videoSurvey"),
        files: recordVideoSurvey,
        isAllowedDelete: false,
        isAllowedEdit: false,
      };

      foldersResult.folders = foldersResult.folders
        ? [...foldersResult.folders, videoSurvey]
        : [videoSurvey];
      folders[foldersResultIndex] = foldersResult;
      setQuoteFolders([...folders]);
    }
  } else {
    const folder = {
      name: translate("videos"),
      type: FolderType.PHOTOS_VIDEOS,
      folders: [
        {
          name: translate("videoSurvey"),
          files: recordVideoSurvey,
          isAllowedDelete: false,
          isAllowedEdit: false,
        },
      ],
      isAllowedDelete: false,
      isAllowedEdit: false,
    };

    setQuoteFolders([...quoteFolders, folder]);
  }
}

export function getOrderFolder(translate: TFunction<"translation", undefined>, order?: Order) {
  const orderUpdated = { ...(order || {}) };

  const orderFolders: Folder[] = [];
  const newFolders: Folder[] = [];

  let hasFolderPhoto: boolean = false;
  let hasFolderVideo: boolean = false;

  if (orderUpdated?.folders && orderUpdated?.folders?.length > 0) {
    orderUpdated?.folders?.forEach((folder) => {
      if (folder?.name?.toLowerCase() === FolderName.VIDEOS) {
        hasFolderVideo = true;
        orderFolders?.push({
          ...folder,
          isAllowedDelete: false,
          isAllowedEdit: false,
          showQuantity: false,
        });
      } else if (folder?.name?.toLowerCase() === FolderName.PHOTOS) {
        hasFolderPhoto = true;
        orderFolders?.push({
          ...folder,
          isAllowedDelete: false,
          isAllowedEdit: false,
          showQuantity: false,
        });
      } else {
        orderFolders.push(folder);
      }
    });
  }

  if (!hasFolderPhoto) {
    newFolders.push({
      name: translate("photos"),
      type: FolderType.PHOTOS_VIDEOS,
      isAllowedDelete: false,
      showQuantity: false,
      isAllowedEdit: false,
    });
  }

  if (!hasFolderVideo) {
    newFolders.push({
      name: translate("videos"),
      type: FolderType.PHOTOS_VIDEOS,
      isAllowedDelete: false,
      showQuantity: false,
      isAllowedEdit: false,
    });
  }

  orderUpdated.folders = [...newFolders, ...orderFolders];

  return orderUpdated;
}

export default function getQuoteFolder(
  translate: TFunction<"translation", undefined>,
  quote?: Sales
) {
  const quoteUpdated = { ...(quote || {}) };

  const quoteFolders: Folder[] = [];
  const newFolders: Folder[] = [];

  let hasFolderPhoto: boolean = false;
  let hasFolderVideo: boolean = false;
  let hasFolderDocument: boolean = false;

  if (quoteUpdated?.folders && quoteUpdated?.folders?.length > 0) {
    // eslint-disable-next-line array-callback-return
    quoteUpdated?.folders?.map((folder) => {
      if (folder?.name?.toLowerCase() === FolderName.VIDEOS) {
        hasFolderVideo = true;
        quoteFolders?.push({
          ...folder,
          isAllowedDelete: false,
          isAllowedEdit: false,
          showQuantity: false,
        });
      } else if (folder?.name?.toLowerCase() === FolderName.PHOTOS) {
        hasFolderPhoto = true;
        quoteFolders?.push({
          ...folder,
          isAllowedDelete: false,
          isAllowedEdit: false,
          showQuantity: false,
        });
      } else if (folder?.name?.toLowerCase() === FolderName.GENERAL) {
        hasFolderDocument = true;
        quoteFolders?.push({
          ...folder,
          isAllowedDelete: false,
          isAllowedEdit: false,
          showQuantity: false,
        });
      } else {
        quoteFolders.push(folder);
      }
    });
  }

  if (!hasFolderPhoto) {
    newFolders.push({
      name: translate("photos"),
      type: FolderType.PHOTOS_VIDEOS,
      isAllowedDelete: false,
      showQuantity: false,
      isAllowedEdit: false,
    });
  }

  if (!hasFolderVideo) {
    newFolders.push({
      name: translate("videos"),
      type: FolderType.PHOTOS_VIDEOS,
      isAllowedDelete: false,
      showQuantity: false,
      isAllowedEdit: false,
    });
  }

  if (!hasFolderDocument) {
    newFolders.push({
      name: translate("general"),
      type: FolderType.DOCUMENTS,
      isAllowedDelete: false,
      showQuantity: false,
      isAllowedEdit: false,
    });
  }

  const updatedFolders = [...newFolders, ...quoteFolders];

  if (
    updatedFolders &&
    updatedFolders?.length > 0 &&
    quote?.quotePictures &&
    quote?.quotePictures?.length > 0
  ) {
    const indexPhotoFolder = updatedFolders.findIndex(
      (folder) => folder?.name?.toLowerCase() === FolderName.PHOTOS
    );

    if (indexPhotoFolder !== -1) {
      const quotePictureFiles: File[] = [];

      quote?.quotePictures?.forEach((picture, index) => {
        quotePictureFiles.push({
          name: `${translate("generalPhoto")} ${index + 1}`,
          type: FileType.IMAGE,
          uri: picture?.mediaLink,
          extension: "png",
          isAllowedDelete: false,
          isAllowedEdit: false,
        });
      });

      updatedFolders[indexPhotoFolder] = {
        ...updatedFolders[indexPhotoFolder],
        files: [...(updatedFolders[indexPhotoFolder]?.files || []), ...quotePictureFiles],
      };
    }
  }

  quoteUpdated.folders = [...updatedFolders];

  return quoteUpdated;
}
